import { FormLoadingIndicator } from 'components/shared/FormLoadingIndicator/FormLoadingIndicator';
import type { AnalyticCategoryActions } from 'models';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { SelectedProductVariation } from 'models/productsReferential';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { getProductReferentialLoadingStatus } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { workspaceProductVariationSelectedThunk } from 'state/workspaces/workspace.thunks';
import { workspaceClientSelected } from 'state/workspaces/workspaces/workspace.actions';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { getSelectedClient, getSelectedProductVariation } from 'state/workspaces/workspaces/workspaces.selector';
import styled from 'styled-components';

import { ClientSelector } from './ClientSelector';
import type { ProductVariationSelectionAutoFocusType } from './ProductVariationSelector';
import { ProductVariationSelector } from './ProductVariationSelector';

export interface ProductAndClientSelectorPros {
  workspace: WorkspaceWithProductSelector;
  canSelectClient?: boolean;
  canSelectPayoffType?: boolean;
  onSubmitted?: (productVariation: SelectedProductVariation, client: Nullable<ISalesCounterpart>) => void;
}
const StyledContentDiv = styled.div`
  height: 95%;
  width: 80%;

  @media screen and (max-width: 576px) {
    height: 40em;
    width: 100%;
  }
`;
export function ProductAndClientSelector({
  workspace,
  canSelectClient = false,
  canSelectPayoffType = false,
  onSubmitted,
}: ProductAndClientSelectorPros) {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch>();

  const loadingStatus = useSelector((state: AppState) => getProductReferentialLoadingStatus(state, workspace));
  const selectedProductVariation = useSelector((state: AppState) =>
    getSelectedProductVariation(state, workspace, intl),
  );
  const selectedClient = useSelector((state: AppState) => getSelectedClient(state, workspace));

  const [autoFocus, setAutoFocus] = useState<ProductVariationSelectionAutoFocusType>('searchInput');
  const [selectorType, setSelectorType] = useState<'productVariation' | 'client'>('productVariation');

  const onSelectClientClick = useCallback(() => {
    setSelectorType('client');
    logAnalytics.trackEvent(
      `Primary market product selector ${workspace}` as keyof AnalyticCategoryActions,
      'Click on button',
      'Select client',
    );
  }, [workspace]);

  let display = null;

  if (loadingStatus === 'not_yet_loaded') {
    return <FormLoadingIndicator className="w-100" />;
  }

  if (selectorType === 'productVariation') {
    display = (
      <ProductVariationSelector
        autoFocus={autoFocus}
        workspace={workspace}
        canSelectClient={canSelectClient}
        canSelectPayoffType={canSelectPayoffType}
        selectedProductVariation={selectedProductVariation}
        selectedClient={selectedClient}
        onProductVariationSelected={(selectedProductVariation, isQuickStart) =>
          isQuickStart
            ? onSubmitted?.(selectedProductVariation as SelectedProductVariation, selectedClient)
            : dispatch(workspaceProductVariationSelectedThunk(workspace, selectedProductVariation, intl))
        }
        onSelectClientClick={onSelectClientClick}
        onClearClientClick={() => dispatch(workspaceClientSelected(workspace, null))}
        onConfirmAndCreateClick={() =>
          onSubmitted?.(selectedProductVariation as SelectedProductVariation, selectedClient)
        }
      />
    );
  }

  if (selectorType === 'client') {
    display = (
      <ClientSelector
        selectedClient={selectedClient}
        onBackClick={() => setSelectorType('productVariation')}
        onCancelClick={() => setSelectorType('productVariation')}
        onClientSelected={client => {
          dispatch(workspaceClientSelected(workspace, client));
          setTimeout(() => setSelectorType('productVariation'), 0);
          setAutoFocus('confirmButton');
        }}
        workspaceType={workspace}
      />
    );
  }

  return (
    <div
      data-e2e={`${workspace}-form-starter-page`}
      className="d-flex justify-content-center align-items-start h-100 overflow-auto py-3"
    >
      <StyledContentDiv className="my-auto">{display}</StyledContentDiv>
    </div>
  );
}
